// The integration's non-schema settings — cache, circuit breaker, automatic retry,
// timeout and auto-enable — with their numeric validation. These are plain
// columns on the integration, never part of the attribute schema.

import { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { t } from '@lingui/macro';

const DEFAULT_TIMEOUT = 15;
const MIN_CACHE_TTL = 300;
const MAX_RETRY_AFTER = 900;
const MIN_TIMEOUT = 1;
const MAX_TIMEOUT = 60;

const digitsOnly = (value) => value.replace(/\D/g, '');

export default function useIntegrationPanels({
  integration,
  setDisableSubmit,
  setError,
}) {
  const attrs = integration.attributes;

  const [cacheEnabled, setCacheEnabled] = useState(attrs.cache_enabled);
  const [cacheTTL, setCacheTTL] = useState(JSON.stringify(attrs.cache_ttl));
  const [showTtlField, setShowTtlField] = useState(attrs.cache_enabled);

  const [circuitBreakerEnabled, setCircuitBreakerEnabled] = useState(
    attrs.circuit_breaker_enabled,
  );
  const [circuitBreakerRetryAfter, setCircuitBreakerRetryAfter] = useState(
    JSON.stringify(attrs.circuit_breaker_retry_after),
  );
  const [circuitBreakerFailureThreshold, setCircuitBreakerFailureThreshold] =
    useState(JSON.stringify(attrs.circuit_breaker_failure_threshold));

  const [automaticRetryEnabled, setAutomaticRetryEnabled] = useState(
    attrs.automatic_retry_enabled,
  );
  const [retryAfter, setRetryAfter] = useState(
    JSON.stringify(attrs.retry_after),
  );
  const [maxRetries, setMaxRetries] = useState(
    JSON.stringify(attrs.max_retries),
  );

  const [timeout, setTimeout] = useState(
    JSON.stringify(attrs.timeout || DEFAULT_TIMEOUT),
  );
  const [autoEnable, setAutoEnable] = useState(attrs.auto_enable ?? false);

  const [maxRetriesWarning, setMaxRetriesWarning] = useState('');
  const [failureThresholdWarning, setFailureThresholdWarning] = useState('');

  // Returns true when it recognised and consumed the field, so the caller knows
  // the edit was a panel edit rather than a schema-attribute edit.
  const handleTextChange = useCallback(
    (e, fieldName = '') => {
      const name = fieldName || e?.target?.name;
      const raw = e?.target?.value ?? '';

      switch (name) {
        case 'retry_after':
          setRetryAfter(Math.min(MAX_RETRY_AFTER, digitsOnly(raw)));
          return true;
        case 'max_retries': {
          const next = digitsOnly(raw);

          if (next === '0') {
            setMaxRetriesWarning('Needs to be a number greater than 0');
            setDisableSubmit(true);
          } else {
            setDisableSubmit(false);
          }

          setMaxRetries(next);
          return true;
        }
        case 'cache_ttl':
          setCacheTTL(digitsOnly(raw));
          return true;
        case 'timeout':
          setTimeout(digitsOnly(raw));
          return true;
        case 'circuit_breaker_retry_after':
          setCircuitBreakerRetryAfter(digitsOnly(raw));
          return true;
        case 'circuit_breaker_failure_threshold': {
          const next = digitsOnly(raw);

          if (next === '0') {
            setFailureThresholdWarning('Needs to be a number greater than 0');
            setDisableSubmit(true);
          } else {
            setDisableSubmit(false);
          }

          setCircuitBreakerFailureThreshold(next);
          return true;
        }
        default:
          return false;
      }
    },
    [setDisableSubmit],
  );

  const validateCacheTTL = useCallback(
    (e) => {
      const { value } = e.target;

      if (isEmpty(trim(value))) {
        setError('cache_ttl', t`Can't be blank`);
        setDisableSubmit(true);
      } else if (Number(value) < MIN_CACHE_TTL) {
        setError('cache_ttl', t`Can't be less than 300 seconds`);
        setDisableSubmit(true);
      } else {
        setError('cache_ttl', '');
        setDisableSubmit(false);
      }
    },
    [setError, setDisableSubmit],
  );

  const validateTimeout = useCallback(
    (e) => {
      const { value } = e.target;

      if (isEmpty(trim(value))) {
        setError('timeout', t`Can't be blank`);
        setDisableSubmit(true);
      } else if (Number(value) < MIN_TIMEOUT) {
        setError('timeout', t`Can't be less than 1 second`);
        setDisableSubmit(true);
      } else if (Number(value) > MAX_TIMEOUT) {
        setError('timeout', t`Can't be greater than 60 seconds`);
        setDisableSubmit(true);
      } else {
        setError('timeout', '');
        setDisableSubmit(false);
      }
    },
    [setError, setDisableSubmit],
  );

  const handleShowTtlField = useCallback(
    (currentErrors) => {
      if (showTtlField && currentErrors.cache_ttl) setDisableSubmit(false);

      if (!showTtlField && Number(cacheTTL) === 0) {
        setCacheTTL(MIN_CACHE_TTL);
        setError('cache_ttl', '');
      } else if (!showTtlField && currentErrors.cache_ttl) {
        setDisableSubmit(true);
      }

      setShowTtlField(!showTtlField);
    },
    [showTtlField, cacheTTL, setDisableSubmit, setError],
  );

  const reset = useCallback((data) => {
    const next = data.attributes;

    setCacheEnabled(next.cache_enabled);
    setShowTtlField(next.cache_enabled);
    setCacheTTL(next.cache_ttl);
    setCircuitBreakerEnabled(next.circuit_breaker_enabled);
    setCircuitBreakerRetryAfter(next.circuit_breaker_retry_after);
    setCircuitBreakerFailureThreshold(next.circuit_breaker_failure_threshold);
    setAutomaticRetryEnabled(next.automatic_retry_enabled);
    setRetryAfter(next.retry_after);
    setMaxRetries(next.max_retries);
    setTimeout(next.timeout || DEFAULT_TIMEOUT);
    setAutoEnable(next.auto_enable ?? false);
  }, []);

  // Exactly the positional argument list updateIntegrationStaticAttributes expects.
  const submitValues = {
    cacheEnabled,
    cacheTTL: cacheEnabled ? cacheTTL : 0,
    circuitBreakerEnabled,
    circuitBreakerFailureThreshold: circuitBreakerEnabled
      ? circuitBreakerFailureThreshold
      : 0,
    circuitBreakerRetryAfter: circuitBreakerEnabled
      ? circuitBreakerRetryAfter
      : 0,
    automaticRetryEnabled,
    retryAfter: automaticRetryEnabled ? retryAfter : 0,
    maxRetries: automaticRetryEnabled ? maxRetries : 0,
    timeout,
    autoEnable,
  };

  return {
    cacheEnabled,
    setCacheEnabled,
    cacheTTL,
    showTtlField,
    circuitBreakerEnabled,
    setCircuitBreakerEnabled,
    circuitBreakerRetryAfter,
    circuitBreakerFailureThreshold,
    automaticRetryEnabled,
    setAutomaticRetryEnabled,
    retryAfter,
    maxRetries,
    timeout,
    autoEnable,
    setAutoEnable,
    maxRetriesWarning,
    failureThresholdWarning,
    handleTextChange,
    handleShowTtlField,
    validateCacheTTL,
    validateTimeout,
    reset,
    submitValues,
  };
}
